import React, { useEffect, useMemo, useState } from 'react';
import { useCart } from '../../contexts/CartProvider';
import styles from './style.module.scss';
import { Button, Input, Divider, Checkbox } from 'antd';
import { BsFillCreditCardFill } from 'react-icons/bs';
import { FaTag } from 'react-icons/fa6';
import { HiOutlineShieldCheck } from 'react-icons/hi';
import CreditCard from '../CartaoGiratorio';
import { api } from '../../services/apiClient';
import { toast } from 'react-toastify';
import { useSaldo } from '../../contexts/SaldoContext';
import { useGirosGanhos } from '../../contexts/GirosGanhosContext';

const CartaoCredito = ({ items, handleNextStep, couponCode, setCouponCode, validarCupon }) => {
  const [isSaldoRoute, setIsSaldoRoute] = useState(false);
  const { clearCart, coupon, setTotal } = useCart();
  const { fetchSaldo } = useSaldo();
  const { mostrarGirosGanhos } = useGirosGanhos();

  const [cardNumber, setCardNumber] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cpf, setCpf] = useState('');
  const [parcelas, setParcelas] = useState('');
  const [loading, setLoading] = useState(false);
  const [cupomAplicado, setCupomAplicado] = useState(false);
  const [aceiteTermos, setAceiteTermos] = useState(false);

  useEffect(() => {
    setIsSaldoRoute(window.location.href.includes('/saldo'));
  }, []);

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  const totals = useMemo(() => items.reduce((acc, item) => {
    if (!acc[item.nome]) acc[item.nome] = 0;
    acc[item.nome] += item.quantidade * item.valor;
    return acc;
  }, {}), [items]);

  const initialGrandTotal = useMemo(() => Object.values(totals).reduce((acc, value) => acc + value, 0), [totals]);
  const totalQuantidade = useMemo(() => items.reduce((acc, item) => acc + item.quantidade, 0), [items]);

  let grandTotal = initialGrandTotal;
  let grandTotalBeforeDiscount = initialGrandTotal;
  let desconto = 0;

  if (coupon && coupon.isValid) {
    if (coupon.is_porcentagem === 1) {
      desconto = initialGrandTotal * (coupon.discount / 100);
      grandTotal -= desconto;
    } else {
      desconto = coupon.discount;
      grandTotal -= desconto;
    }
  }

  const handleCupon = async () => {
    if (!couponCode?.trim()) {
      toast.warn('Digite um cupom para aplicar.');
      return;
    }

    const validar = await validarCupon(couponCode);
    if (validar) {
      setCupomAplicado(true);
      toast.success('Cupom aplicado com sucesso!');
      return;
    }

    toast.error('Cupom inválido.');
  };

  const handlePayCard = async () => {
    setLoading(true);

    if (!cardNumber || !cardHolderName || !expiryDate || !cvv || !cpf || !parcelas) {
      toast.warn('Por favor, preencha todos os dados do cartão.', { position: 'top-right', autoClose: 5000, theme: 'light' });
      setLoading(false);
      return;
    }

    if (!aceiteTermos) {
      toast.warn('Por favor, aceite os termos de uso e a política de privacidade.', { position: 'top-right', autoClose: 5000, theme: 'dark' });
      setLoading(false);
      return;
    }

    const dataTokenCard = JSON.stringify({
      card_number: cardNumber.replace(/\D/g, ''),
      cardholder: { name: cardHolderName, identification: { type: 'CPF', number: cpf.replace(/\D/g, '') } },
      security_code: cvv,
      expiration_month: expiryDate.split('/')[0],
      expiration_year: `20${expiryDate.split('/')[1]}`,
    });

    try {
      const tokenResponse = await fetch('https://api.mercadopago.com/v1/card_tokens?public_key=APP_USR-c69facd8-08eb-4dfc-b299-f86744ee816a', {
        method: 'POST',
        body: dataTokenCard,
        headers: { 'Content-Type': 'application/json' },
      });
      const resultToken = await tokenResponse.json();

      const issuerResponse = await fetch(`https://api.mercadopago.com/v1/payment_methods/search?public_key=APP_USR-c69facd8-08eb-4dfc-b299-f86744ee816a&locale=pt-BR&bins=${resultToken.first_six_digits}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await issuerResponse.json();

      if (!result.results?.length) {
        setLoading(false);
        return;
      }

      const issuerId = result.results[0].issuer.id;
      const paymentMethodId = result.results[0].id;

      if (isSaldoRoute) {
        const responsePayment = await api.post('/carrega_saldo', {
          cpfCnpjCartaoCredito: cpf.replace(/\D/g, ''),
          nomeTitularCartao: cardHolderName,
          quantidadeParcelasPagamento: parcelas,
          tipoDocumento: 'CPF',
          tokenCard: resultToken.id,
          issuerId,
          paymentMethodId,
          total: grandTotal,
        });

        setTotal(grandTotal);

        if (responsePayment.status === 201) {
          toast.success('Compra feita com sucesso', { position: 'top-right', autoClose: 5000, theme: 'light' });
          fetchSaldo();
          handleNextStep(responsePayment.data.id);
          clearCart();
        }

        setLoading(false);
        return;
      }

      const responsePayment = await api.post('/comprar', {
        cupom_desconto_id: coupon?.id,
        boloes: items,
        quantity: totalQuantidade,
        cpfCnpjCartaoCredito: cpf.replace(/\D/g, ''),
        nomeTitularCartao: cardHolderName,
        quantidadeParcelasPagamento: parcelas,
        total: grandTotalBeforeDiscount,
        tokenCard: resultToken.id,
        issuerId,
        paymentMethodId,
      });

      if (responsePayment.status === 201) {
        if (responsePayment.data.status === 'rejected') {
          toast.error('Falha ao realizar a compra. Verifique se os dados do cartão estão corretos.', { position: 'top-right', autoClose: 5000, theme: 'light' });
          setLoading(false);
          return;
        }

        if (responsePayment.data.status !== 'in_process') {
          toast.success('Compra feita com sucesso', { position: 'top-right', autoClose: 5000, theme: 'light' });
          clearCart();
          window.scrollTo(0, 0);
          fetchSaldo();
          handleNextStep(responsePayment.data.id);
          mostrarGirosGanhos(responsePayment.data.giros_creditados);
        }
      }
    } catch (error) {
      console.log('Erro ao processar pagamento', error);
      toast.error('Erro ao processar pagamento', { position: 'top-right', autoClose: 5000, theme: 'dark' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.checkoutPage}>
      <div className={styles.grid}>
        <section className={styles.leftColumn}>
          {!isSaldoRoute && (
            <div className={styles.cardBox}>
              <div className={styles.sectionTitle}><FaTag /><h2>Você tem algum cupom de desconto?</h2></div>
              <p className={styles.sectionDescription}>Digite abaixo para aplicar e atualizar o total do pedido.</p>
              <div className={styles.couponRow}>
                <Input placeholder='Digite seu cupom' value={couponCode} onChange={(e) => setCouponCode(e.target.value)} disabled={cupomAplicado} />
              </div>
                <Button className={styles.applyButton} onClick={handleCupon} disabled={cupomAplicado}>{cupomAplicado ? 'Aplicado' : 'Aplicar cupom'}</Button>
            </div>
          )}

          <div className={styles.cardBox}>
            <div className={styles.sectionTitle}><BsFillCreditCardFill /><h2>Dados do cartão</h2></div>
            <p className={styles.sectionDescription}>Preencha exatamente como no cartão para evitar recusas.</p>
            <CreditCard
              cardNumber={cardNumber}
              setCardNumber={setCardNumber}
              cardHolderName={cardHolderName}
              setCardHolderName={setCardHolderName}
              expiryDate={expiryDate}
              setExpiryDate={setExpiryDate}
              cvv={cvv}
              setCvv={setCvv}
              cpf={cpf}
              setCpf={setCpf}
              parcelas={parcelas}
              setParcelas={setParcelas}
            />
          </div>

          <div className={styles.securityBox}>
            <HiOutlineShieldCheck />
            <div>
              <strong>Ambiente protegido</strong>
              <p>Seus dados são processados em ambiente seguro.</p>
            </div>
          </div>

        </section>

        <aside className={styles.rightColumn}>
          <div className={styles.summaryCard}>
            <h2>Resumo do pedido</h2>
            <div className={styles.summaryItems}>
              {Object.keys(totals).map((nome) => (
                <div key={nome} className={styles.summaryRow}><span>{nome}</span><strong>{formatCurrency(totals[nome])}</strong></div>
              ))}
              {coupon && coupon.isValid && (
                <div className={`${styles.summaryRow} ${styles.discountRow}`}><span>Cupom ({couponCode})</span><strong>-{formatCurrency(desconto)}</strong></div>
              )}
            </div>
            <Divider className={styles.divider} />
            <div className={styles.totalRow}><span>Total</span><h3>{formatCurrency(grandTotal)}</h3></div>
            <Button loading={loading} disabled={loading} className={styles.confirmButton} onClick={handlePayCard} block>Confirmar compra</Button>
            <p className={styles.warningText}>Após finalizar a compra, não será possível estorno automático.</p>
          </div>

          <div className={styles.termsRow}>
            <Checkbox checked={aceiteTermos} onChange={(e) => setAceiteTermos(e.target.checked)}>
              Li e concordo com os <strong>Termos de Uso</strong> e <strong>Política de Privacidade</strong>
            </Checkbox>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CartaoCredito;
